import React, { useState } from 'react';
import { TextField, Button, Box, Typography, MenuItem } from '@mui/material';
import useSnackbar from '../../../hooks/useSnackbar';
import { DialogBoxComp } from '../../atoms/DialogBox';
import {
  HOLIDAY_TYPE_LABELS,
  HolidayBody,
  HolidayType,
  IHoliday,
  POSTHoliday,
  PUTHoliday,
} from '../../../services/holiday';
import { DateField } from '../../../pages/Booking/Details';
import dayjs from 'dayjs';

const holidayTypes = (
  Object.entries(HOLIDAY_TYPE_LABELS) as [HolidayType, string][]
).map(([value, label]) => ({ value, label }));

type Props = {
  /** When provided the form edits the holiday instead of creating one */
  holiday?: IHoliday | null;
  onSaved?: (holiday: IHoliday) => void;
  handleDrawerClose?: () => void;
};

const AddHolidayForm = ({ holiday, onSaved, handleDrawerClose }: Props) => {
  const isEdit = Boolean(holiday?._id);
  const [formData, setFormData] = useState<HolidayBody>({
    date: holiday?.date ? dayjs(holiday.date).format('YYYY-MM-DD') : '',
    type: holiday?.type || 'PUBLIC_HOLIDAY',
    description: holiday?.description || '',
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.date || !formData.type) {
      showSnackbar('Date and type are required', 'error');
      return;
    }
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const saved = isEdit
        ? await PUTHoliday(holiday!._id, formData)
        : await POSTHoliday(formData);

      showSnackbar(isEdit ? 'Holiday updated!' : 'Holiday created!', 'success');
      setTimeout(() => {
        onSaved?.(saved);
        handleDrawerClose?.();
      }, 500);
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Error occurred.', 'error');
    } finally {
      setSaving(false);
      setDialogOpen(false);
    }
  };

  const handleReset = () => {
    setFormData({
      date: holiday?.date ? dayjs(holiday.date).format('YYYY-MM-DD') : '',
      type: holiday?.type || 'PUBLIC_HOLIDAY',
      description: holiday?.description || '',
    });
  };

  return (
    <Box sx={{ padding: 0, flex: 1 }}>
      {SnackbarComponent}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1">Date</Typography>
        <DateField
          fullWidth
          type="date"
          name="date"
          value={formData.date}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="caption" color="#8A8D91">
          Must be today or an upcoming date. Sundays are not allowed (weekly
          off already covers them).
        </Typography>

        <Typography variant="subtitle1">Holiday Type</Typography>
        <TextField
          select
          name="type"
          value={formData.type}
          onChange={handleChange}
          required
        >
          {holidayTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Typography variant="subtitle1">Description (Optional)</Typography>
        <TextField
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          minRows={3}
          placeholder="e.g. Diwali"
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleReset} disabled={saving}>
            Reset
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {isEdit ? 'Update' : 'Submit'}
          </Button>
        </Box>
      </Box>

      <DialogBoxComp
        open={dialogOpen}
        title={isEdit ? 'Confirm Holiday Update' : 'Confirm Holiday'}
        content={
          isEdit
            ? 'Are you sure you want to update this holiday?'
            : 'Are you sure you want to add this holiday? It applies to all technicians.'
        }
        handleClose={() => setDialogOpen(false)}
        handleConfirm={handleConfirm}
      />
    </Box>
  );
};

export default AddHolidayForm;
